import { css } from '@linaria/core'

import * as colors from './colors'

export interface ThemeDefinition {
  // backgrounds
  backgroundSurfacePrimary: string
  backgroundSurfacePrimaryStrong: string
  backgroundSurfaceSecondary: string
  backgroundSurfaceDisabled: string
  backgroundSurfaceTertiary: string
  backgroundSurfaceTertiaryStrong: string
  backgroundSurfaceStandardSubtle: string
  backgroundSurfaceStandardDefault: string
  backgroundSurfaceStandardStrong: string
  backgroundSurfaceTransparent: string
  backgroundSurfaceOverlay: string
  backgroundSurfaceAccentDefault: string
  backgroundSurfaceAccentStrong: string
  backgroundSurfaceAccentSubtle: string
  backgroundSemanticInfo: string
  backgroundSemanticSuccess: string
  backgroundSemanticWarning: string
  backgroundSemanticDanger: string
  // lines / borders
  borderDefault: string
  borderDisabled: string
  borderSurface: string
  borderTransparent: string
  borderOutline: string
  borderInput: string
  borderHover: string
  borderInfo: string
  borderSuccess: string
  borderWarning: string
  borderDanger: string
  // text
  textDefault: string
  textStrong: string
  textSoft: string
  textSubtle: string
  textDisabled: string
  textInverse: string
  textWhite: string
  textAccent: string
  textInfo: string
  textSuccess: string
  textWarning: string
  textDanger: string
}

const defaultTheme: ThemeDefinition = {
  // backgrounds
  backgroundSurfacePrimary: colors.white,
  backgroundSurfacePrimaryStrong: colors.white,
  backgroundSurfaceSecondary: colors.silver050,
  backgroundSurfaceDisabled: colors.silver200,
  backgroundSurfaceTertiary: colors.silver200,
  backgroundSurfaceTertiaryStrong: colors.silver300,
  backgroundSurfaceStandardDefault: colors.anthracite800,
  backgroundSurfaceStandardStrong: colors.anthracite900,
  backgroundSurfaceStandardSubtle: colors.anthracite050,
  backgroundSurfaceTransparent: colors.transparent,
  backgroundSurfaceOverlay: colors.overlay,
  backgroundSurfaceAccentDefault: colors.manred500,
  backgroundSurfaceAccentStrong: colors.manred600,
  backgroundSurfaceAccentSubtle: colors.manred050,
  backgroundSemanticInfo: colors.blue100,
  backgroundSemanticSuccess: colors.green100,
  backgroundSemanticWarning: colors.yellow100,
  backgroundSemanticDanger: colors.red100,
  // lines / borders
  borderDefault: colors.anthracite500,
  borderDisabled: colors.silver400,
  borderSurface: colors.anthracite100,
  borderTransparent: colors.transparent,
  borderOutline: colors.blue700,
  borderInput: colors.anthracite500,
  borderHover: colors.anthracite900,
  borderInfo: colors.blue900,
  borderSuccess: colors.green900,
  borderWarning: colors.yellow900,
  borderDanger: colors.red500,
  // text
  textDefault: colors.anthracite800,
  textStrong: colors.black,
  textSoft: colors.anthracite600,
  textSubtle: colors.silver300,
  textDisabled: colors.silver400,
  textInverse: colors.white,
  textWhite: colors.white,
  textAccent: colors.manred500,
  textInfo: colors.blue900,
  textSuccess: colors.green900,
  textWarning: colors.anthracite800,
  textDanger: colors.red700,
}

const highContrastTheme: ThemeDefinition = {
  // backgrounds
  backgroundSurfacePrimary: colors.white,
  backgroundSurfacePrimaryStrong: colors.white,
  backgroundSurfaceSecondary: colors.silver100,
  backgroundSurfaceDisabled: colors.silver200,
  backgroundSurfaceTertiary: colors.silver200,
  backgroundSurfaceTertiaryStrong: colors.silver400,
  backgroundSurfaceStandardDefault: colors.anthracite800,
  backgroundSurfaceStandardStrong: colors.anthracite900,
  backgroundSurfaceStandardSubtle: colors.anthracite100,
  backgroundSurfaceTransparent: colors.transparent,
  backgroundSurfaceOverlay: colors.overlay,
  backgroundSurfaceAccentDefault: colors.manred500,
  backgroundSurfaceAccentStrong: colors.manred800,
  backgroundSurfaceAccentSubtle: colors.manred050,
  backgroundSemanticInfo: colors.blue100,
  backgroundSemanticSuccess: colors.green100,
  backgroundSemanticWarning: colors.yellow100,
  backgroundSemanticDanger: colors.red100,
  // lines / borders
  borderDefault: colors.anthracite600,
  borderDisabled: colors.silver400,
  borderSurface: colors.anthracite200,
  borderTransparent: colors.transparent,
  borderOutline: colors.blue700,
  borderInput: colors.anthracite600,
  borderHover: colors.anthracite900,
  borderInfo: colors.blue900,
  borderSuccess: colors.green900,
  borderWarning: colors.yellow900,
  borderDanger: colors.red500,
  // text
  textDefault: colors.anthracite900,
  textStrong: colors.black,
  textSoft: colors.anthracite600,
  textSubtle: colors.silver500,
  textDisabled: colors.silver600,
  textInverse: colors.white,
  textWhite: colors.white,
  textAccent: colors.manred500,
  textInfo: colors.blue900,
  textSuccess: colors.green900,
  textWarning: colors.anthracite800,
  textDanger: colors.red700,
}

const darkTheme: ThemeDefinition = {
  // backgrounds
  backgroundSurfacePrimary: colors.black,
  backgroundSurfacePrimaryStrong: colors.silver855,
  backgroundSurfaceSecondary: colors.silver800,
  backgroundSurfaceDisabled: colors.silver750,
  backgroundSurfaceTertiary: colors.silver750,
  backgroundSurfaceTertiaryStrong: colors.silver700,
  backgroundSurfaceStandardDefault: colors.anthracite400,
  backgroundSurfaceStandardStrong: colors.anthracite500,
  backgroundSurfaceStandardSubtle: colors.anthracite700,
  backgroundSurfaceTransparent: colors.transparent,
  backgroundSurfaceOverlay: colors.overlay,
  backgroundSurfaceAccentDefault: colors.manred500,
  backgroundSurfaceAccentStrong: colors.manred800,
  backgroundSurfaceAccentSubtle: colors.manred900,
  backgroundSemanticInfo: colors.blue900,
  backgroundSemanticSuccess: colors.green900,
  backgroundSemanticWarning: colors.yellow950,
  backgroundSemanticDanger: colors.red800,
  // lines
  borderDefault: colors.anthracite500,
  borderDisabled: colors.silver600,
  borderSurface: colors.anthracite700,
  borderTransparent: colors.transparent,
  borderOutline: colors.blue900,
  borderInput: colors.anthracite500,
  borderHover: colors.anthracite300,
  borderInfo: colors.blue700,
  borderSuccess: colors.green700,
  borderWarning: colors.yellow700,
  borderDanger: colors.red500,
  // text
  textDefault: colors.white,
  textStrong: colors.white,
  textSoft: colors.anthracite300,
  textSubtle: colors.silver600,
  textDisabled: colors.silver500,
  textInverse: colors.anthracite800,
  textWhite: colors.white,
  textAccent: colors.manred500,
  textInfo: colors.blue300,
  textSuccess: colors.green300,
  textWarning: colors.yellow200,
  textDanger: colors.red100,
} as const

export const theme = {
  backgroundSurfacePrimary: 'var(--background-surface-primary)',
  backgroundSurfacePrimaryStrong: 'var(--background-surface-primary-strong)',
  backgroundSurfaceSecondary: 'var(--background-surface-secondary)',
  backgroundSurfaceDisabled: 'var(--background-surface-disabled)',
  backgroundSurfaceTertiary: 'var(--background-surface-tertiary)',
  backgroundSurfaceTertiaryStrong: 'var(--background-surface-tertiary-strong)',
  backgroundSurfaceStandardSubtle: 'var(--background-surface-standard-subtle)',
  backgroundSurfaceStandardDefault: 'var(--background-surface-standard-default)',
  backgroundSurfaceStandardStrong: 'var(--background-surface-standard-strong)',
  backgroundSurfaceTransparent: 'var(--background-surface-transparent)',
  backgroundSurfaceOverlay: 'var(--background-surface-overlay)',
  backgroundSurfaceAccentDefault: 'var(--background-surface-accent-default)',
  backgroundSurfaceAccentStrong: 'var(--background-surface-accent-strong)',
  backgroundSurfaceAccentSubtle: 'var(--background-surface-accent-subtle)',
  backgroundSemanticInfo: 'var(--background-semantic-info)',
  backgroundSemanticSuccess: 'var(--background-semantic-success)',
  backgroundSemanticWarning: 'var(--background-semantic-warning)',
  backgroundSemanticDanger: 'var(--background-semantic-danger)',
  borderDefault: 'var(--border-default)',
  borderDisabled: 'var(--border-disabled)',
  borderSurface: 'var(--border-surface)',
  borderTransparent: 'var(--border-transparent)',
  borderOutline: 'var(--border-outline)',
  borderInput: 'var(--border-input)',
  borderHover: 'var(--border-hover)',
  borderInfo: 'var(--border-info)',
  borderSuccess: 'var(--border-success)',
  borderWarning: 'var(--border-warning)',
  borderDanger: 'var(--border-danger)',
  textDefault: 'var(--text-default)',
  textStrong: 'var(--text-strong)',
  textSoft: 'var(--text-soft)',
  textSubtle: 'var(--text-subtle)',
  textDisabled: 'var(--text-disabled)',
  textInverse: 'var(--text-inverse)',
  textWhite: 'var(--text-white)',
  textAccent: 'var(--text-accent)',
  textInfo: 'var(--text-info)',
  textSuccess: 'var(--text-success)',
  textWarning: 'var(--text-warning)',
  textDanger: 'var(--text-danger)',
} as const satisfies Record<keyof ThemeDefinition, string>

const generateThemeCssVariables = (theme: ThemeDefinition) => {
  return `
    --background-surface-primary: ${theme.backgroundSurfacePrimary};
    --background-surface-primary-strong: ${theme.backgroundSurfacePrimaryStrong};
    --background-surface-secondary: ${theme.backgroundSurfaceSecondary};
    --background-surface-disabled: ${theme.backgroundSurfaceDisabled};
    --background-surface-tertiary: ${theme.backgroundSurfaceTertiary};
    --background-surface-tertiary-strong: ${theme.backgroundSurfaceTertiaryStrong};
    --background-surface-standard-subtle: ${theme.backgroundSurfaceStandardSubtle};
    --background-surface-standard-default: ${theme.backgroundSurfaceStandardDefault};
    --background-surface-standard-strong: ${theme.backgroundSurfaceStandardStrong};
    --background-surface-transparent: ${theme.backgroundSurfaceTransparent};
    --background-surface-overlay: ${theme.backgroundSurfaceOverlay};
    --background-surface-accent-default: ${theme.backgroundSurfaceAccentDefault};
    --background-surface-accent-strong: ${theme.backgroundSurfaceAccentStrong};
    --background-surface-accent-subtle: ${theme.backgroundSurfaceAccentSubtle};
    --background-semantic-info: ${theme.backgroundSemanticInfo};
    --background-semantic-success: ${theme.backgroundSemanticSuccess};
    --background-semantic-warning: ${theme.backgroundSemanticWarning};
    --background-semantic-danger: ${theme.backgroundSemanticDanger};    
    --border-default: ${theme.borderDefault};
    --border-disabled: ${theme.borderDisabled};
    --border-surface: ${theme.borderSurface};
    --border-transparent: ${theme.borderTransparent};
    --border-outline: ${theme.borderOutline};
    --border-input: ${theme.borderInput};
    --border-hover: ${theme.borderHover};
    --border-info: ${theme.borderInfo};
    --border-success: ${theme.borderSuccess};
    --border-warning: ${theme.borderWarning};
    --border-danger: ${theme.borderDanger};
    --text-default: ${theme.textDefault};
    --text-strong: ${theme.textStrong};
    --text-soft: ${theme.textSoft};
    --text-subtle: ${theme.textSubtle};
    --text-disabled: ${theme.textDisabled};
    --text-inverse: ${theme.textInverse};
    --text-white: ${theme.textWhite};
    --text-accent: ${theme.textAccent};
    --text-info: ${theme.textInfo};
    --text-success: ${theme.textSuccess};
    --text-warning: ${theme.textWarning};
    --text-danger: ${theme.textDanger}` as const
}

// this injects the css variables into the global scope automatically
export const themeCss = css`
  :global() {
    body,
    body[data-theme='default'] {
      ${generateThemeCssVariables(defaultTheme)}
    }

    body[data-theme='dark'] {
      ${generateThemeCssVariables(darkTheme)}
    }

    body[data-theme='high-contrast'] {
      ${generateThemeCssVariables(highContrastTheme)}
    }
  }
`

// css`
//   // usage
//
//   .my-page {
//     background-color: ${theme.background};
//     color: ${theme.textColor};
//   }
//
//   .my-filter-header {
//     background-color: ${theme.emphasizedBackground};
//     color: ${theme.emphasizedText};
//   }
// `
